import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import Modal from '@/components/Modal';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Chip from '@/components/Chip';
import LoadingState from '@/components/LoadingState';
import Button from '@/components/Button';
import { DocumentsAPI } from '@/services/documentsAPI';
import { Document, DocumentAudit } from '@/types/documents';

interface AuditModalProps {
  document: Document;
  onClose: () => void;
}

const ACTION_COLORS: Record<
  string,
  'success' | 'warning' | 'danger' | undefined
> = {
  created: 'success',
  updated: undefined,
  deactivated: 'danger',
  reactivated: 'success',
  version_uploaded: 'success',
  reordered: undefined,
  signature_position_set: 'success',
};

const AuditModal: React.FC<AuditModalProps> = ({ document, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [audits, setAudits] = useState<DocumentAudit[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAudits();
  }, [document.id]);

  const fetchAudits = async () => {
    setLoading(true);
    try {
      const response = await DocumentsAPI.getAudits(document.id);
      if (response.success) {
        setAudits(response.data);
      } else {
        setError('Failed to load audit log');
      }
    } catch (err: any) {
      setError('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const formatChanges = (
    change_details: Record<string, unknown> | null,
  ): string => {
    if (!change_details) return '';
    return Object.entries(change_details)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}: ${value[0]} → ${value[1]}`;
        }
        return `${key}: ${JSON.stringify(value)}`;
      })
      .join(', ');
  };

  return (
    <Modal
      size="lg"
      title={`Audit Log - ${document.name}`}
      onRequestClose={onClose}
    >
      <Card.Section>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <Body>{error}</Body>
        ) : (
          <Stack gap={12} vertical>
            {audits.length === 0 ? (
              <Body>No audit entries found.</Body>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>
                      Action
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>
                      Details
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Admin</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>IP</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {audits.map((audit) => (
                    <tr
                      key={audit.id}
                      style={{ borderBottom: '1px solid #eee' }}
                    >
                      <td style={{ padding: '8px' }}>
                        <Chip
                          color={ACTION_COLORS[audit.action]}
                          label={audit.action}
                          size="small"
                        />
                      </td>
                      <td style={{ padding: '8px', maxWidth: '300px' }}>
                        <Small>{formatChanges(audit.change_details)}</Small>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <Small>
                          {audit.admin_name || `ID: ${audit.admin_user_id}`}
                        </Small>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <Small>{audit.ip_address || 'N/A'}</Small>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <Small>
                          {audit.created_at
                            ? format(
                                new Date(audit.created_at),
                                'MMM dd, yyyy HH:mm',
                              )
                            : 'N/A'}
                        </Small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Stack>
        )}
      </Card.Section>
      <Card.Section subdued>
        <Stack justify="end">
          <Button
            a11yLabel="Close"
            appearance="outline"
            label="Close"
            onClick={onClose}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default AuditModal;
